import { createTheme } from "@mui/material/styles";

// 👑 DARK VAPORWAVE / KING JESUS DESIGN SYSTEM
// Inspired by: Dark Space, Bright Cyan, Hot Magenta
// Palette: Deep Space Indigo + Neon Cyan/Magenta + Silver/White

export const designSystem = {
  brand: {
    name: "WORSHIP.RO",
    tagline: "Your Music Learning Journey",
    description: "Professional music education platform",
  },

  colors: {
    // PRIMARY: Light Silver / Grey (For text and icons)
    primary: {
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
      900: "#0f172a",
      main: "#e2e8f0",
      light: "#f8fafc",
      dark: "#94a3b8",
      gradient: "linear-gradient(135deg, #f8fafc 0%, #cbd5e1 100%)",
      soft: "linear-gradient(135deg, rgba(248, 250, 252, 0.1) 0%, rgba(203, 213, 225, 0.05) 100%)",
      glow: "0 0 20px rgba(248, 250, 252, 0.2)",
    },

    // 🎨 SECONDARY: Hot Magenta / Pink - Chromatic accent
    secondary: {
      50: "#fdf4ff",
      100: "#fae8ff",
      200: "#f5d0fe",
      300: "#f0abfc",
      400: "#e879f9",
      500: "#d946ef", // Main Magenta
      600: "#c026d3",
      700: "#a21caf",
      800: "#86198f",
      900: "#701a75",
      main: "#d946ef",
      light: "#f0abfc",
      dark: "#c026d3",
      gradient: "linear-gradient(135deg, #f0abfc 0%, #d946ef 100%)",
      soft: "linear-gradient(135deg, rgba(217, 70, 239, 0.15) 0%, rgba(192, 38, 211, 0.05) 100%)",
      glow: "0 0 20px rgba(217, 70, 239, 0.4)",
    },

    // ⚡ ACCENT: Bright Cyan - The lion's edge
    accent: {
      main: "#22d3ee", // Cyan 400
      light: "#67e8f9",
      dark: "#06b6d4",
      gradient: "linear-gradient(135deg, #67e8f9 0%, #22d3ee 100%)",
      soft: "linear-gradient(135deg, rgba(34, 211, 238, 0.15) 0%, rgba(6, 182, 212, 0.05) 100%)",
      glow: "0 0 20px rgba(34, 211, 238, 0.4)",
    },

    // 🌌 BACKGROUND: Deep Lavender/Indigo Space
    background: {
      ground: "#06060a",   // The deepest void
      base: "#09090e",     // Default body background
      raised: "#12121a",   // Sidebar, drawers
      surface: "#181824",  // Cards, modals, dropdowns
      overlay: "#1e1e2d",  // Hover states

      // Aliases (for MUI + legacy)
      primary: "#09090e",
      secondary: "#12121a",
      main: "#09090e",
      paper: "#12121a",
      elevated: "#181824",
      glass: "rgba(15, 15, 27, 0.7)",
      light: "rgba(255, 255, 255, 0.04)",
      lighter: "rgba(255, 255, 255, 0.02)",
      gradient: "linear-gradient(180deg, #09090e 0%, #06060a 100%)",

      hero: "radial-gradient(circle at 50% 0%, #1a1a2e 0%, #09090e 100%)",
      card: "linear-gradient(145deg, rgba(24, 24, 36, 0.8) 0%, rgba(18, 18, 26, 0.9) 100%)",
    },

    // 📝 TEXT: Bright text for dark background
    text: {
      primary: "#ffffff",   // Pure white
      secondary: "#94a3b8", // Slate 400
      muted: "#64748b",     // Slate 500
      disabled: "#475569",  // Slate 600
      gradient: "linear-gradient(to right, #ffffff 0%, #cbd5e1 100%)",
      highlight: "linear-gradient(to right, #22d3ee 0%, #d946ef 100%)",
    },

    // 💎 SURFACE: Dark Glass
    surface: {
      main: "rgba(255, 255, 255, 0.05)",
      elevated: "rgba(255, 255, 255, 0.08)",
      card: "rgba(255, 255, 255, 0.03)",
      border: "rgba(255, 255, 255, 0.08)",
      borderMedium: "rgba(255, 255, 255, 0.15)",
      borderBright: "rgba(34, 211, 238, 0.3)",
      light: "rgba(255, 255, 255, 0.02)",
      hover: "rgba(255, 255, 255, 0.08)",
      active: "rgba(34, 211, 238, 0.15)",
      stroke: "rgba(255, 255, 255, 0.1)",
    },
  },

  success: {
    main: "#10b981", light: "#34d399", dark: "#059669",
    gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    glow: "0 0 20px rgba(16, 185, 129, 0.3)",
  },
  warning: {
    main: "#f59e0b", light: "#fbbf24", dark: "#d97706",
    gradient: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
    glow: "0 0 20px rgba(245, 158, 11, 0.3)",
  },
  error: {
    main: "#f43f5e", light: "#fb7185", dark: "#e11d48", 
    gradient: "linear-gradient(135deg, #fb7185 0%, #e11d48 100%)",
    glow: "0 0 20px rgba(244, 63, 94, 0.3)",
  },
  info: {
    main: "#0ea5e9", light: "#38bdf8", dark: "#0284c7",
    gradient: "linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)",
    glow: "0 0 20px rgba(14, 165, 233, 0.3)",
  },

  teacher: {
    background: "linear-gradient(135deg, #09090e 0%, #12121a 100%)",
    cards: {
      courses: "linear-gradient(135deg, #181824 0%, #12121a 100%)",
      tutorials: "linear-gradient(135deg, #1e1e2d 0%, #181824 100%)",
      students: "linear-gradient(135deg, rgba(34, 211, 238, 0.05) 0%, rgba(34, 211, 238, 0.02) 100%)",
      revenue: "linear-gradient(135deg, rgba(217, 70, 239, 0.05) 0%, rgba(217, 70, 239, 0.02) 100%)",
      analytics: "linear-gradient(135deg, #181824 0%, #09090e 100%)",
      sessions: "linear-gradient(135deg, #1e1e2d 0%, #12121a 100%)",
    },
    glassmorphism: {
      background: "rgba(15, 15, 27, 0.6)",
      backdropFilter: "blur(20px)",
      border: "1px solid rgba(255, 255, 255, 0.08)",
    },
  },

  // Legacy support aliases
  background: { main: "#09090e", paper: "#12121a", elevated: "#181824", glass: "rgba(15, 15, 27, 0.7)", light: "rgba(255, 255, 255, 0.04)", lighter: "rgba(255, 255, 255, 0.02)" },
  primary: { main: "#e2e8f0", gradient: "linear-gradient(135deg, #f8fafc 0%, #cbd5e1 100%)", glow: "0 0 20px rgba(248, 250, 252, 0.2)" },
  secondary: { main: "#d946ef", gradient: "linear-gradient(135deg, #f0abfc 0%, #d946ef 100%)" },
  text: { primary: "#ffffff", secondary: "#94a3b8", muted: "#64748b", disabled: "#475569" },
  surface: { main: "rgba(255, 255, 255, 0.05)", elevated: "rgba(255, 255, 255, 0.08)", light: "rgba(255, 255, 255, 0.02)", border: "rgba(255, 255, 255, 0.08)", borderBright: "rgba(34, 211, 238, 0.3)", hover: "rgba(255, 255, 255, 0.08)", card: "rgba(255, 255, 255, 0.03)", stroke: "rgba(255, 255, 255, 0.1)" },
  border: "rgba(255, 255, 255, 0.08)",

  spacing: ["0px", "4px", "8px", "12px", "16px", "20px", "24px", "32px", "40px", "48px", "64px"],
  borderRadius: { xs: "6px", sm: "8px", md: "12px", lg: "16px", xl: "24px", "2xl": "32px", full: "999px" },
  
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h1: { fontFamily: "'Nunito', sans-serif", fontWeight: 800 },
    h2: { fontFamily: "'Nunito', sans-serif", fontWeight: 800 },
    h3: { fontFamily: "'Nunito', sans-serif", fontWeight: 700 },
    h4: { fontFamily: "'Nunito', sans-serif", fontWeight: 600 },
    h5: { fontFamily: "'Nunito', sans-serif", fontWeight: 600 },
    h6: { fontFamily: "'Nunito', sans-serif", fontWeight: 600 },
    sizes: { xs: "0.75rem", sm: "0.875rem", md: "1rem", lg: "1.125rem", xl: "1.25rem", "2xl": "1.5rem", "3xl": "1.875rem", "4xl": "2.25rem" },
    weights: { regular: 400, medium: 500, semiBold: 600, bold: 700, black: 800, extraBlack: 900 },
  },

  shadows: {
    xs: "0 1px 2px rgba(0, 0, 0, 0.3)",
    sm: "0 2px 4px rgba(0, 0, 0, 0.4)",
    md: "0 4px 12px rgba(0, 0, 0, 0.5)",
    lg: "0 12px 24px rgba(0, 0, 0, 0.6)",
    xl: "0 20px 40px rgba(0, 0, 0, 0.7)",
    glowPrimary: "0 0 20px rgba(34, 211, 238, 0.4)",
    glowSecondary: "0 0 20px rgba(217, 70, 239, 0.4)",
    glowAccent: "0 0 20px rgba(139, 92, 246, 0.4)",
    inner: "inset 0 1px 3px rgba(0, 0, 0, 0.5)",
    aberration: "-2px 0px 0px #06b6d4, 2px 0px 0px #d946ef", // Solid sharp shadow for text
  },

  neo: {
    palette: {
      chromatic: ["#f8fafc", "#f0abfc", "#22d3ee", "#c026d3", "#06b6d4", "#e2e8f0"],
      harmony: ["#22d3ee", "#f0abfc", "#c026d3", "#06b6d4", "#d946ef"],
      rhythm: ["#d946ef", "#f0abfc", "#22d3ee", "#e2e8f0"],
      cyan: "#22d3ee", magenta: "#f0abfc", violet: "#d946ef", amber: "#c026d3",
      surface: { base: "rgba(15, 15, 27, 0.7)", card: "rgba(24, 24, 36, 0.6)", elevated: "rgba(30, 30, 45, 0.8)", stroke: "rgba(255, 255, 255, 0.08)", strokeBright: "rgba(34, 211, 238, 0.28)" },
    },
    gradients: {
      symphony: "linear-gradient(135deg, #c4b5fd 0%, #f0abfc 25%, #67e8f9 50%, #e879f9 75%, #22d3ee 100%)",
      chord: "radial-gradient(circle at 30% 40%, #1a1a2e 0%, #0f0f1a 35%, #06060a 70%, #000000 100%)",
      melody: "conic-gradient(from 45deg, #09090e 0%, #12121a 20%, #181824 40%, #1e1e2d 60%, #12121a 80%, #09090e 100%)",
      perfectFifth: "linear-gradient(135deg, #22d3ee 0%, #f0abfc 100%)",
      majorThird: "linear-gradient(135deg, #22d3ee 0%, #c026d3 100%)",
      minorSeventh: "linear-gradient(135deg, #22d3ee 0%, #f0abfc 50%, #c026d3 100%)",
      crescendo: "radial-gradient(ellipse at center, rgba(34, 211, 238, 0.15) 0%, rgba(139, 92, 246, 0.08) 50%, transparent 100%)",
      decrescendo: "radial-gradient(ellipse at center, rgba(139, 92, 246, 0.12) 0%, rgba(217, 70, 239, 0.06) 70%, transparent 100%)",
      sheetMusic: "linear-gradient(0deg, transparent 0%, rgba(34, 211, 238, 0.02) 50%, transparent 100%)",
      soundWave: "radial-gradient(ellipse 60% 30% at 50% 50%, rgba(217, 70, 239, 0.08) 0%, transparent 70%)",
      cardHarmony: "linear-gradient(145deg, rgba(24, 24, 36, 0.8) 0%, rgba(18, 18, 26, 0.9) 50%, rgba(12, 12, 18, 0.95) 100%)",
      cardMelody: "radial-gradient(circle at 20% 80%, rgba(34, 211, 238, 0.05) 0%, transparent 50%), linear-gradient(135deg, rgba(24, 24, 36, 0.7) 0%, rgba(18, 18, 26, 0.8) 100%)",
      aurora: "linear-gradient(135deg, #22d3ee 0%, #f0abfc 50%, #c026d3 100%)",
    },
    glass: {
      base: "rgba(15, 15, 27, 0.6)", highlight: "rgba(255, 255, 255, 0.08)", dark: "rgba(0, 0, 0, 0.4)", stroke: "rgba(255, 255, 255, 0.08)", strokeBright: "rgba(34, 211, 238, 0.3)",
    },
    glow: {
      subtle: "0 2px 8px rgba(0, 0, 0, 0.3)",
      soft: "0 4px 16px rgba(0, 0, 0, 0.4)",
      moderate: "0 8px 24px rgba(34, 211, 238, 0.15)",
      intense: "0 0 20px rgba(34, 211, 238, 0.25)",
      harmonic: "0 0 16px rgba(139, 92, 246, 0.1), 0 0 32px rgba(217, 70, 239, 0.1)",
      rhythmic: "0 0 20px rgba(217, 70, 239, 0.1), 0 0 36px rgba(139, 92, 246, 0.08)",
      musical: "0 0 24px rgba(34, 211, 238, 0.1), 0 0 16px rgba(217, 70, 239, 0.1), 0 0 32px rgba(139, 92, 246, 0.06)",
    },
    blur: { light: "blur(12px)", medium: "blur(20px)", heavy: "blur(32px)" },
  },
};

// MUI Theme Configuration
const theme = createTheme({
  palette: {
    mode: "dark", // CHANGED TO DARK MODE
    primary: {
      main: designSystem.colors.accent.main, 
      light: designSystem.colors.accent.light,
      dark: designSystem.colors.accent.dark,
      contrastText: "#000000",
    },
    secondary: {
      main: designSystem.colors.secondary.main,
      light: designSystem.colors.secondary.light,
      dark: designSystem.colors.secondary.dark,
      contrastText: "#000000",
    },
    background: {
      default: designSystem.colors.background.main,
      paper: designSystem.colors.background.paper,
    },
    text: {
      primary: designSystem.colors.text.primary,
      secondary: designSystem.colors.text.secondary,
      disabled: designSystem.colors.text.disabled,
    },
    success: { main: designSystem.success.main, light: designSystem.success.light, dark: designSystem.success.dark },
    warning: { main: designSystem.warning.main, light: designSystem.warning.light, dark: designSystem.warning.dark },
    error: { main: designSystem.error.main, light: designSystem.error.light, dark: designSystem.error.dark },
    info: { main: designSystem.info.main, light: designSystem.info.light, dark: designSystem.info.dark },
  },
  typography: {
    fontFamily: designSystem.typography.fontFamily,
    button: { textTransform: "none", fontWeight: 600 },
  },
  shape: { borderRadius: 16 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 12, fontWeight: 600, padding: "10px 24px" },
        containedPrimary: {
          background: "#6366f1", // Normal Indigo
          boxShadow: "0 4px 14px 0 rgba(99, 102, 241, 0.39)",
          color: "#ffffff",
          "&:hover": { 
            background: "#4f46e5", 
            boxShadow: "0 0 20px rgba(34, 211, 238, 0.6)", // Neon cyan glow on hover!
            transform: "translateY(-1px)" 
          },
        },
        containedSecondary: {
          background: designSystem.colors.secondary.gradient,
          boxShadow: designSystem.shadows.glowSecondary,
          color: "#000000",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: designSystem.colors.background.paper,
          borderRadius: 12,
          border: `1px solid ${designSystem.surface.border}`,
          boxShadow: designSystem.shadows.md,
        },
      },
    },
    MuiContainer: { styleOverrides: { root: { maxWidth: "1200px !important" } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: designSystem.colors.background.main,
          color: designSystem.colors.text.primary,
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": { background: "#09090e" },
          "&::-webkit-scrollbar-thumb": { background: "rgba(255, 255, 255, 0.2)", borderRadius: "4px" },
          "&::-webkit-scrollbar-thumb:hover": { background: "rgba(255, 255, 255, 0.3)" },
        },
      },
    },
  },
});

// Extend theme with custom properties
theme.background = designSystem.background;
theme.primary = designSystem.primary;
theme.secondary = designSystem.secondary;
theme.text = designSystem.text;
theme.surface = designSystem.surface;
theme.border = designSystem.border;
theme.neo = designSystem.neo;
theme.success = designSystem.success;
theme.warning = designSystem.warning;
theme.error = designSystem.error;
theme.info = designSystem.info;
theme.colors = designSystem.colors;

export default theme;
